import { useCallback, useState } from "react";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { selectableColors } from "@/design/colors/colors";
import Avatar from "@/design/components/Avatar";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import ColorPicker from "@/design/forms/ColorPicker";
import FormGroup from "@/design/forms/FormGroup";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import Valkyrie, {
  viArrowLeft,
  viArrowRight,
  viCalendar,
  viCalendarClock,
  viSpinnerThird,
  viTrashCan,
} from "@sippy-platform/valkyrie";

import { Button, CardHeader, Dialog, Grid, Stack } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { isAfter, isSameSecond, parseISO, startOfDay, subSeconds } from "date-fns";
import PropTypes from "prop-types";

import holidayReducer from "./holidayReducer";

function HolidayDialog({ closeHolidayDialog, holidaySet, holidaySetDispatch, ...props }) {
  const [fullDay, setFullDay] = useState(
    isSameSecond(props.holiday.start, startOfDay(props.holiday.start)) &&
      isSameSecond(props.holiday.end, startOfDay(props.holiday.end)),
  );
  const [deleting, setDeleting] = useState(false);

  const {
    value: holiday,
    dispatch,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: props.holiday,
    mode: SaveMode.MANUAL,
    reducer: holidayReducer,
    validator: () => ({
      name: string().required({ message: "Name is required." }),
      start: string().required({ message: "Start date is required." }),
      end: string().required({ message: "End date is required." }).truthy(isAfter(holiday.end, holiday.start), {
        message: "The end of the holiday must be the set after the start of the holiday.",
      }),
      color: string().required({ message: "Color is required." }),
    }),
    onSubmit: () => Api.storeHolidaySetHoliday(holidaySet, [holiday]),
    onSuccess: (response) => {
      if (!holiday.id) {
        dispatch({ type: "id", id: response.data.holidays[0].id });
        holidaySetDispatch({ type: "addHoliday", holiday: { ...holiday, id: response.data.holidays[0].id } });
      } else {
        holidaySetDispatch({ type: "updateHoliday", holiday });
      }

      closeHolidayDialog();
    },
  });

  const formHandler = (property, value) => {
    switch (property) {
      case "start":
      case "end":
        dispatch({ type: property, value, fullDay });
        break;
      case "startBlur":
      case "endBlur":
        // Shenanigans with onBlur and onAccept
        dispatch({ type: property, value: value?.target?.value, event: value, fullDay });
        break;
      case "fullDay":
        setFullDay((prev) => !prev);
        dispatch({ type: property, fullDay: !fullDay });
        break;
      default:
        dispatch({ type: property, value });
    }
  };

  const deleteHoliday = useCallback(async () => {
    setDeleting(true);

    Promise.resolve(Api.deleteHolidaySetHoliday(holidaySet, holiday))
      .then(() => {
        holidaySetDispatch({ type: "deleteHoliday", holiday });
        closeHolidayDialog();
        setFullDay(true);
      })
      .finally(() => setDeleting(false));
  }, [closeHolidayDialog, holiday, holidaySet, holidaySetDispatch]);

  return (
    <>
      <Dialog onClose={closeHolidayDialog} open={!!props.holiday} maxWidth="sm">
        <HeaderCard>
          <CardHeader
            avatar={
              <Avatar text={holiday.id ? "Edit holiday" : "New holiday"} icon={viCalendar} color={holiday.color} />
            }
            title={holiday.id ? "Edit holiday" : "New holiday"}
          />
        </HeaderCard>
        <Stack
          sx={{
            gap: 2,
            mt: 2,
          }}
        >
          <FormGroup title="Settings">
            <Grid size={12}>
              <InputText required id="name" label="Name" onChange={formHandler} value={holiday.name} error={errors} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DateTimePicker
                closeOnSelect={false}
                showDaysOutsideCurrentMonth
                label="Start date"
                required
                autoOk
                id="start"
                value={parseISO(holiday.start)}
                format={fullDay ? "dd-MM-yyyy" : "dd-MM-yyyy HH:mm"}
                views={fullDay ? ["year", "month", "day"] : ["year", "month", "day", "hours", "minutes"]}
                onChange={(e) => formHandler("start", e)}
                onAccept={(e) => formHandler("startBlur", e)}
                slots={{
                  leftArrowIcon: () => <Valkyrie icon={viArrowLeft} />,
                  openPickerIcon: () => <Valkyrie icon={fullDay ? viCalendar : viCalendarClock} />,
                  rightArrowIcon: () => <Valkyrie icon={viArrowRight} />,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    onBlur: (e) => formHandler("startBlur", e),
                    error: !!errors?.start,
                    helperText: errors?.start?.[0],
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <InputToggle
                id="fullDay"
                error={errors}
                onChange={formHandler}
                states={[false, true]}
                value={fullDay}
                primary="Full day"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <DateTimePicker
                closeOnSelect={false}
                showDaysOutsideCurrentMonth
                label="End date"
                required
                autoOk
                id="end"
                value={fullDay ? subSeconds(parseISO(holiday.end), 1) : parseISO(holiday.end)}
                format={fullDay ? "dd-MM-yyyy" : "dd-MM-yyyy HH:mm"}
                views={fullDay ? ["year", "month", "day"] : ["year", "month", "day", "hours", "minutes"]}
                onChange={(e) => formHandler("end", e)}
                onAccept={(e) => formHandler("endBlur", e)}
                slots={{
                  leftArrowIcon: () => <Valkyrie icon={viArrowLeft} />,
                  openPickerIcon: () => <Valkyrie icon={fullDay ? viCalendar : viCalendarClock} />,
                  rightArrowIcon: () => <Valkyrie icon={viArrowRight} />,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    onBlur: (e) => formHandler("endBlur", e),
                    error: !!errors?.end,
                    helperText: errors?.end?.[0],
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <OverflowDrawer
                label="Destination"
                value={holiday.overflow}
                propertyName="overflow"
                overflowHandler={(value) => formHandler("overflow", value)}
              />
            </Grid>
            <Grid size={12}>
              <ColorPicker colors={selectableColors} select={(e) => formHandler("color", e)} value={holiday.color} />
            </Grid>
          </FormGroup>
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
              gap: 1,
              containerType: "inline-size",
            }}
          >
            {holiday.id && (
              <Button
                onClick={deleteHoliday}
                color="error"
                variant="outlined"
                startIcon={<Valkyrie icon={deleting ? viSpinnerThird : viTrashCan} spin={deleting} />}
                disabled={deleting}
              >
                Delete
              </Button>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: "flex-end",
                gap: 1,
                ml: "auto",
              }}
            >
              <Button onClick={closeHolidayDialog} variant="outlined" disabled={deleting}>
                Discard
              </Button>
              <SaveIndicator formState={formState} errors={allErrors} save={save} disabled={deleting} />
            </Stack>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}

HolidayDialog.propTypes = {
  holidaySet: PropTypes.object,
  holiday: PropTypes.object,
  closeHolidayDialog: PropTypes.func,
  holidaySetDispatch: PropTypes.func,
};

export default HolidayDialog;
